/**
 * ModernAdminContent - Enhanced admin content with modern tabs and sections
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  Users, Calendar, Mail, Image as ImageIcon, Settings, History, Music,
  Plus, Pencil, Trash2, Filter, Search, MoveUp, MoveDown, Video, User, RefreshCw, LogOut, X, Utensils, Table, Ticket, ShieldAlert 
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { useAdmin } from './AdminHooks';
import ModernDashboardOverview from './ModernDashboardOverview';
import ModernReservationsTab from './ModernReservationsTab';
import ModernEventsTab from './ModernEventsTab';
import ModernOrdersTab from './ModernOrdersTab';
import ModernTablesTab from './ModernTablesTab';
import ModernClientsTab from './ModernClientsTab';
import ModernMediaLibraryTab from './ModernMediaLibraryTab';
import ModernNotificationsTab from './ModernNotificationsTab';
import ModernCalendarTab from './ModernCalendarTab';
import ModernAdminsTab from './ModernAdminsTab';
import ModernHistoryTab from './ModernHistoryTab';
import ModernMusicPlayerTab from './ModernMusicPlayerTab';
import ModernCodesTab from './ModernCodesTab';
import { cn } from '../../lib/utils';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback } from '../ui/avatar';

const ModernAdminContent = ({ className }) => {
  const {
    events, clients, loading, stats, emailsUnread, emailsQueued,
    adminActiveTab, setAdminActiveTab, user, logout,
    fetchBootData, fetchData, fetchReservations, fetchClientsOnly, fetchStatsOnly,
    fetchOrders, ordersEventId, fetchTables, tablesEventId,
    fetchLibrary, fetchFeaturedMedia, fetchEmailsUnread, fetchEmailsQueued,
    sidebarOpen, setSidebarOpen
  } = useAdmin();

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.matchMedia('(max-width: 767.98px)').matches; } catch { return false; }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMd, setIsMd] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.matchMedia('(min-width: 768px) and (max-width: 1023.98px)').matches; } catch { return false; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const mq = window.matchMedia('(max-width: 767.98px)');
      const handler = (e) => setIsMobile(e.matches);
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.onchange = handler;
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.onchange = null;
      };
    } catch {}
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const mq = window.matchMedia('(min-width: 768px) and (max-width: 1023.98px)');
      const handler = (e) => setIsMd(e.matches);
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.onchange = handler;
      setIsMd(mq.matches);
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.onchange = null;
      };
    } catch {}
  }, []);
  useEffect(() => {
    if (isMd) setSidebarOpen(false);
  }, [isMd]);
  useEffect(() => {
    if (adminActiveTab === 'notifications') {
      setSidebarOpen(false);
    }
  }, [adminActiveTab]);

  const tabs = [
    { value: 'reservations', label: 'Réservations', icon: Ticket },
    { value: 'orders', label: 'Commandes', icon: Utensils },
    { value: 'tables', label: 'Tables', icon: Table },
    { value: 'codes', label: 'Codes', icon: Ticket },
    { value: 'events', label: 'Événements', icon: Calendar },
    { value: 'calendar', label: 'Calendrier', icon: Calendar },
    { value: 'media', label: 'Médias', icon: ImageIcon },
    { value: 'music', label: 'Musique', icon: Music },
    { value: 'clients', label: 'Clients', icon: User },
    { value: 'notifications', label: 'Emails', icon: Mail, count: emailsUnread },
    { value: 'admins', label: 'Admins', icon: ShieldAlert },
    { value: 'history', label: 'Historique', icon: History },
  ];
  const allowed = user?.role === 'owner' ? tabs.map(t => t.value) : (user?.allowedTabs || []);
  const visibleTabs = tabs.filter(t => allowed.includes(t.value));
  const primaryValues = ['reservations', 'orders', 'tables', 'clients', 'notifications'];
  const primaryTabs = visibleTabs.filter(t => primaryValues.includes(t.value));
  const hasMore = visibleTabs.length > primaryTabs.length;
 

  return (
    <div className={cn("", className)}>
      {isMobile ? (
        <div className="md:hidden">
          <section className="pb-16">
            <ModernDashboardOverview />
            {adminActiveTab === 'reservations' && <ModernReservationsTab />}
            {adminActiveTab === 'orders' && <ModernOrdersTab />}
            {adminActiveTab === 'tables' && <ModernTablesTab />}
            {adminActiveTab === 'codes' && <ModernCodesTab />}
            {adminActiveTab === 'events' && <ModernEventsTab />}
            {adminActiveTab === 'calendar' && <ModernCalendarTab />}
            {adminActiveTab === 'media' && <ModernMediaLibraryTab />}
            {adminActiveTab === 'music' && <ModernMusicPlayerTab />}
            {adminActiveTab === 'clients' && <ModernClientsTab />}
            {adminActiveTab === 'notifications' && <ModernNotificationsTab />}
            {adminActiveTab === 'admins' && <ModernAdminsTab />}
            {adminActiveTab === 'history' && <ModernHistoryTab />}
          </section>
          <nav className="fixed right-0 bottom-0 left-0 z-50 border-t backdrop-blur-md bg-slate-900/80 border-slate-700 md:hidden">
            <div className="flex items-stretch h-14">
              {primaryTabs.map((tab, idx) => {
                const Icon = tab.icon;
                const active = adminActiveTab === tab.value;
                return (
                  <button
                    key={tab.value}
                    onClick={() => setAdminActiveTab(tab.value)}
                    className={cn(
                      "relative flex-1 inline-flex justify-center items-center h-full",
                      idx > 0 ? "border-l border-slate-700" : "",
                      active ? "text-white bg-slate-800" : "text-slate-300 hover:text-white hover:bg-slate-800/50"
                    )}
                    aria-label={tab.value}
                  >
                    <span className={cn("absolute inset-x-0 top-0 h-0.5", active ? "bg-gradient-to-r from-blue-600 to-purple-600" : "bg-transparent")} />
                    <Icon size={20} />
                    {tab.value === 'notifications' && (typeof emailsQueued === 'number') && emailsQueued > 0 && (
                      <span className="inline-block absolute top-2 right-2 w-2 h-2 bg-yellow-400 rounded-full" />
                    )}
                  </button>
                );
              })}
              {hasMore && (
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className={cn(
                    "inline-flex relative flex-1 justify-center items-center h-full border-l border-slate-700",
                    "text-slate-300 hover:text-white hover:bg-slate-800/50"
                  )}
                  aria-label="more"
                  title="Plus"
                >
                  <span className="absolute inset-x-0 top-0 h-0.5 bg-transparent" />
                  <Settings size={20} />
                </button>
              )}
            </div>
          </nav>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetContent side="left" className="bg-slate-900 border-slate-700">
              <SheetHeader>
                <SheetTitle className="text-white">Menu</SheetTitle>
                <SheetDescription className="text-slate-400">
                  Menu de navigation de l’administration
                </SheetDescription>
              </SheetHeader>
              <div className="flex flex-col gap-2 mt-4">
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = adminActiveTab === tab.value;
                  return (
                    <Button
                      key={tab.value}
                      variant="ghost"
                      onClick={() => {
                        setAdminActiveTab(tab.value);
                        setMobileMenuOpen(false);
                      }}
                      className={cn(
                        "justify-start px-3 py-2 w-full rounded",
                        isActive
                          ? "text-white bg-gradient-to-r from-blue-600/30 to-purple-600/30"
                          : "text-slate-300 hover:text-white hover:bg-slate-800"
                      )}
                    >
                      <Icon size={16} className="mr-2" />
                      <span>{tab.label}</span>
                      <span className="inline-flex gap-1 items-center ml-auto">
                        {tab.count !== undefined && (
                          <Badge className="bg-slate-700 text-slate-300 border-slate-600">
                            {tab.count}
                          </Badge>
                        )}
                        {tab.value === 'notifications' && (typeof emailsQueued === 'number') && emailsQueued > 0 && (
                          <Badge className="text-yellow-300 bg-yellow-600/20 border-yellow-600/40">
                            {emailsQueued}
                          </Badge>
                        )}
                      </span>
                    </Button>
                  );
                })}
                <div className="pt-2 mt-2 border-t border-slate-700" />
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "justify-start px-3 py-2 w-full rounded",
                    "text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  )}
                  title="Déconnexion"
                >
                  <LogOut size={16} className="mr-2" />
                  <span>Déconnecter</span>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      ) : (
        <div className="hidden gap-x-4 md:flex sm:gap-x-6 lg:gap-x-8">
          <div
            className="relative transition-all duration-300 ease-out"
            style={{ width: sidebarOpen ? (isMd ? '12rem' : '16rem') : '0.75rem' }}
          >
            <div className="sticky top-16 bg-transparent border-r md:top-20 border-slate-700">
              {sidebarOpen ? (
                <div className="px-3 py-3 border-b border-slate-700">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2 items-center">
                      <Button 
                        onClick={() => {
                          fetchBootData();
                          const t = adminActiveTab;
                          if (t === 'reservations') {
                            fetchReservations(true);
                          } else if (t === 'orders') {
                            if (ordersEventId) fetchOrders(ordersEventId);
                          } else if (t === 'tables') {
                            if (tablesEventId) fetchTables(tablesEventId);
                          } else if (t === 'events') {
                            // events already refreshed by fetchBootData
                          } else if (t === 'clients') {
                            fetchClientsOnly(true);
                          } else if (t === 'media') {
                            fetchLibrary();
                            fetchFeaturedMedia();
                          } else if (t === 'history') {
                            fetchStatsOnly(true);
                          }
                          fetchEmailsUnread();
                          fetchEmailsQueued();
                        }}
                        variant="ghost"
                        size="sm"
                        className="px-3 py-2 rounded text-slate-300 hover:text-white hover:bg-slate-800"
                        title="Actualiser"
                        aria-label="Actualiser"
                      >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                      </Button>
                      <div className="text-left">
                        <div className="text-xs text-slate-400">{user?.email || '—'}</div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSidebarOpen(false)}
                      className="text-slate-300 hover:text-white hover:bg-slate-800"
                      aria-label="Fermer"
                      title="Fermer"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                </div>
              ) : null}
              {sidebarOpen ? (
                <div className="flex flex-col p-2">
                  <div className="flex flex-col gap-1">
                    {visibleTabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = adminActiveTab === tab.value;
                      return (
                        <Button
                          key={tab.value}
                          variant="ghost"
                          onClick={() => setAdminActiveTab(tab.value)}
                          className={cn(
                            "justify-start px-3 py-2 w-full rounded",
                            isActive
                              ? "text-white bg-gradient-to-r from-blue-600/30 to-purple-600/30"
                              : "text-slate-300 hover:text-white hover:bg-slate-800"
                          )}
                        >
                          <Icon size={16} className="mr-2" />
                          <span>{tab.label}</span>
                          <span className="inline-flex gap-1 items-center ml-auto">
                            {tab.count !== undefined && (
                              <Badge className="bg-slate-700 text-slate-300 border-slate-600">
                                {tab.count}
                              </Badge>
                            )}
                            {tab.value === 'notifications' && (typeof emailsQueued === 'number') && emailsQueued > 0 && (
                              <Badge className="text-yellow-300 bg-yellow-600/20 border-yellow-600/40">
                                {emailsQueued}
                              </Badge>
                            )}
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                  <div className="pt-2 mt-2 border-t border-slate-700" />
                  <Button
                    onClick={logout}
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "justify-start px-3 py-2 w-full rounded",
                      "text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    )}
                    title="Déconnexion"
                  >
                    <LogOut size={16} className="mr-2" />
                    <span>Déconnecter</span>
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="absolute top-0 right-0 w-3 h-24 rounded-l-md border-l bg-slate-700/50 hover:bg-slate-600/60 border-slate-600"
                  aria-label="Ouvrir le menu"
                  title="Ouvrir le menu"
                />
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <section className="pb-16 md:pb-0">
              <ModernDashboardOverview />
              {adminActiveTab === 'reservations' && <ModernReservationsTab />}
              {adminActiveTab === 'orders' && <ModernOrdersTab />}
              {adminActiveTab === 'tables' && <ModernTablesTab />}
              {adminActiveTab === 'codes' && <ModernCodesTab />}
              {adminActiveTab === 'events' && <ModernEventsTab />}
              {adminActiveTab === 'calendar' && <ModernCalendarTab />}
              {adminActiveTab === 'media' && <ModernMediaLibraryTab />}
              {adminActiveTab === 'music' && <ModernMusicPlayerTab />}
              {adminActiveTab === 'clients' && <ModernClientsTab />}
              {adminActiveTab === 'notifications' && <ModernNotificationsTab />}
              {adminActiveTab === 'admins' && <ModernAdminsTab />}
              {adminActiveTab === 'history' && <ModernHistoryTab />}
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

ModernAdminContent.propTypes = {
  className: PropTypes.string,
};

export default ModernAdminContent;
